import React from 'react';
import {
  BarChart3, Calendar, TrendingUp, TrendingDown, Minus, Brain, Heart, Moon, Utensils,
  Activity, Target, Dumbbell, Footprints, Zap, Move, Bike, Trophy, Timer, Flame, Stethoscope
} from 'lucide-react';

/** Render markdown bold (*text* or **text**) as <strong> elements */
function renderMarkdown(text: string): React.ReactNode {
  const parts = text.split(/(\*{1,2}[^*]+\*{1,2})/g);
  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*{1,2}([^*]+)\*{1,2}$/);
    if (boldMatch) {
      return <strong key={i}>{boldMatch[1]}</strong>;
    }
    return part;
  });
}

type CedarReportMode = 'mock' | 'report';

interface CedarReportHealthContext {
  sleep_insights: string;
  nutrition_insights: string;
  recovery_patterns?: string;
  performance_correlations: string;
}

interface DeviceAttribution {
  device: string;
  manufacturer: string;
}

interface SpotlightMetric {
  label: string;
  value: string;
  delta?: string;
  delta_direction?: 'positive' | 'negative' | 'neutral';
  context: string;
}

interface DashboardMetric {
  label: string;
  value: string;
  icon: string;
  color: 'success' | 'warning' | 'info' | 'neutral';
  subtitle: string;
}

interface CedarReportData {
  week_start_date: string;
  week_summary: string;
  generated_at?: string;
  tone?: string;
  insights_direction?: string;
  consistency_score?: string;
  report_version?: number;
  momentum_score?: number;
  momentum_direction?: 'rising' | 'steady' | 'dipping';
  spotlight_metric?: SpotlightMetric;
  dashboard_metrics?: DashboardMetric[];
  health_context?: CedarReportHealthContext;
  meta?: {
    device_attribution?: DeviceAttribution[];
  };
}

const mock: CedarReportData = {
  week_start_date: '2024-12-16',
  week_summary:
    "Outstanding progress this week! Your consistency is building momentum, and your engagement with recovery work is paying dividends. Let's dive into what's working and where to focus next.",
  generated_at: '2024-12-18',
  tone: 'encouraging',
  insights_direction: 'Upward',
  consistency_score: '85%',
  health_context: {
    sleep_insights:
      'Your 7+ hour sleep nights correlate with higher-energy workouts. One short sleep night preceded a lower-intensity day.',
    nutrition_insights:
      'Your pre‑workout protein timing appears to support steadier energy during strength circuits.',
    performance_correlations:
      'Mobility sessions were followed by better range of motion in subsequent strength work.'
  }
};

function formatWeekOf(dateStr: string) {
  // Append T00:00:00 to date-only strings to force local timezone parsing
  // (bare ISO dates like "2024-12-16" are parsed as UTC, causing day shifts near midnight)
  const normalized = dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return 'Week of';
  return `Week of ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  activity: <Activity size={18} />, heart: <Heart size={18} />, moon: <Moon size={18} />,
  utensils: <Utensils size={18} />, target: <Target size={18} />, dumbbell: <Dumbbell size={18} />,
  footprints: <Footprints size={18} />, zap: <Zap size={18} />, move: <Move size={18} />,
  bike: <Bike size={18} />, trophy: <Trophy size={18} />, timer: <Timer size={18} />,
  flame: <Flame size={18} />, 'trending-up': <TrendingUp size={18} />, calendar: <Calendar size={18} />,
};

const COLOR_MAP: Record<string, string> = {
  success: '#22C55E', warning: '#F59E0B', info: '#3B82F6', neutral: '#9CA3AF',
};

/** Build exactly 3 stat widgets from the best available data */
function buildThreeStats(data: CedarReportData, mode: CedarReportMode): { icon: React.ReactNode; value: string; label: string }[] {
  const stats: { icon: React.ReactNode; value: string; label: string }[] = [];

  // 1) Pull from dashboard_metrics first (most useful real data)
  if (data.dashboard_metrics && data.dashboard_metrics.length > 0) {
    for (const m of data.dashboard_metrics) {
      if (stats.length >= 3) break;
      stats.push({
        icon: <span style={{ color: COLOR_MAP[m.color] || '#9CA3AF' }}>{ICON_MAP[m.icon] || <Activity size={18} />}</span>,
        value: m.value,
        label: m.label,
      });
    }
  }

  // 2) Add spotlight metric if we still need more
  if (stats.length < 3 && data.spotlight_metric) {
    stats.push({
      icon: <span style={{ color: '#3B82F6' }}><TrendingUp size={18} /></span>,
      value: data.spotlight_metric.value,
      label: data.spotlight_metric.label,
    });
  }

  // 3) Add momentum score if we still need more
  if (stats.length < 3 && data.momentum_score != null) {
    const dirColor = data.momentum_direction === 'rising' ? '#22C55E'
      : data.momentum_direction === 'dipping' ? '#F59E0B' : '#3B82F6';
    const DirIcon = data.momentum_direction === 'rising' ? TrendingUp
      : data.momentum_direction === 'dipping' ? TrendingDown : Minus;
    stats.push({
      icon: <span style={{ color: dirColor }}><DirIcon size={18} /></span>,
      value: String(data.momentum_score),
      label: 'Momentum',
    });
  }

  // 4) Derive boxes from health_context presence to ensure we always show 3
  if (stats.length < 3 && data.health_context?.sleep_insights) {
    stats.push({ icon: <Moon size={18} />, value: 'Tracked', label: 'Sleep' });
  }
  if (stats.length < 3 && data.health_context?.performance_correlations) {
    stats.push({ icon: <Activity size={18} />, value: 'Active', label: 'Training' });
  }
  if (stats.length < 3 && data.health_context?.nutrition_insights) {
    stats.push({ icon: <Utensils size={18} />, value: 'Tracked', label: 'Nutrition' });
  }

  // 5) Fallback to v1 fields
  if (stats.length < 3 && data.consistency_score) {
    stats.push({ icon: <Calendar size={18} />, value: mode === 'mock' ? '5/7' : data.consistency_score, label: mode === 'mock' ? 'Days Active' : 'Consistency' });
  }
  if (stats.length < 3 && data.insights_direction) {
    stats.push({ icon: <TrendingUp size={18} />, value: data.insights_direction, label: 'Direction' });
  }
  if (stats.length < 3 && data.tone) {
    stats.push({ icon: <Brain size={18} />, value: mode === 'mock' ? 'High' : data.tone, label: mode === 'mock' ? 'Engagement' : 'Tone' });
  }

  return stats.slice(0, 3);
}

export const CedarReportDrawerContent: React.FC<{
  mode?: CedarReportMode;
  report?: CedarReportData | null;
}> = ({ mode = 'mock', report }) => {
  const data = mode === 'mock' ? mock : (report || null);

  if (!data) {
    return (
      <div className="insights-card">
        <div className="insights-card-title">
          <BarChart3 size={20} style={{ color: '#1f6c4f' }} />
          Cedar Report
        </div>
        <div className="insights-card-body">Report data isn't available yet.</div>
      </div>
    );
  }

  const threeStats = buildThreeStats(data, mode);

  return (
    <div className="cedar-report">
      <div className="cedar-report-hero">
        <div className="cedar-report-hero-row">
          <div className="cedar-report-hero-icon" aria-hidden="true">
            <BarChart3 size={22} />
          </div>
          <div className="cedar-report-hero-text">
            <div className="cedar-report-hero-title">Cedar Report</div>
            <div className="cedar-report-hero-sub">{formatWeekOf(data.week_start_date)}</div>
          </div>
        </div>
      </div>

      {/* 3 Equal Stat Boxes — always at the top, above summary */}
      {threeStats.length > 0 && (
        <div className="cedar-report-metrics">
          {threeStats.map((stat, i) => (
            <div key={i} className="cedar-report-metric">
              {stat.icon}
              <div className="cedar-report-metric-value">{stat.value}</div>
              <div className="cedar-report-metric-label">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {data.week_summary && (
        <div className="cedar-report-hero-summary">
          <div className="cedar-report-health-title" style={{ marginBottom: '6px' }}>Summary</div>
          <div>{renderMarkdown(data.week_summary)}</div>
        </div>
      )}

      {!!data.health_context && (
        <div className="insights-card">
          <div className="insights-card-title">
            <Heart size={20} style={{ color: '#1f6c4f' }} />
            Health &amp; Performance
          </div>
          <div className="cedar-report-health">
            {data.health_context.sleep_insights && (
              <div className="cedar-report-health-row">
                <Moon size={16} style={{ color: '#1f6c4f' }} />
                <div>
                  <div className="cedar-report-health-title">Sleep</div>
                  <div className="cedar-report-health-body">{renderMarkdown(data.health_context.sleep_insights)}</div>
                </div>
              </div>
            )}
            {data.health_context.nutrition_insights && (
              <div className="cedar-report-health-row">
                <Utensils size={16} style={{ color: '#1f6c4f' }} />
                <div>
                  <div className="cedar-report-health-title">Nutrition</div>
                  <div className="cedar-report-health-body">{renderMarkdown(data.health_context.nutrition_insights)}</div>
                </div>
              </div>
            )}
            {data.health_context.recovery_patterns && (
              <div className="cedar-report-health-row">
                <Stethoscope size={16} style={{ color: '#1f6c4f' }} />
                <div>
                  <div className="cedar-report-health-title">Health</div>
                  <div className="cedar-report-health-body">{renderMarkdown(data.health_context.recovery_patterns)}</div>
                </div>
              </div>
            )}
            {data.health_context.performance_correlations && (
              <div className="cedar-report-health-row">
                <TrendingUp size={16} style={{ color: '#1f6c4f' }} />
                <div>
                  <div className="cedar-report-health-title">Performance</div>
                  <div className="cedar-report-health-body">{renderMarkdown(data.health_context.performance_correlations)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {data.meta?.device_attribution && data.meta.device_attribution.length > 0 && (
        <div className="cedar-report-footer" style={{ fontStyle: 'italic', fontSize: '11px' }}>
          Data from {data.meta.device_attribution.map(d => d.device).join(' and ')}
        </div>
      )}

      <div className="cedar-report-footer">
        {data.generated_at ? `Generated ${new Date(data.generated_at).toLocaleDateString()}` : ' '}
      </div>
    </div>
  );
};
